import { supabase } from './supabase';

interface Session {
  access_token: string;
  expires_at?: number;  // Make expires_at optional to match Supabase types
  user?: {
    email?: string;
  };
}

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

export interface ApiResponse<T = any> {
  ok: boolean;
  data?: T;
  error?: string;
  detail?: string;
  statusCode: number;
}

export interface UpdateUserData {
  name?: string;
  phone?: string;
  role?: number;
  org_id?: number;
}

class Api {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async getValidSession(): Promise<Session | null> {
    // Try to get current session first
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return null;
    }

    // Check if token is about to expire (5 minutes buffer)
    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
    const fiveMinutes = 5 * 60 * 1000;
    
    if (Date.now() + fiveMinutes >= expiresAt) {
      // Only try to refresh if we're close to expiration
      const { data: { session: newSession } } = await supabase.auth.refreshSession();
      return newSession || null;
    }

    return session;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    try {
      // Skip session check for auth endpoints
      const isAuthEndpoint = path.includes('/auth/') || path.includes('/login');
      
      if (!isAuthEndpoint) {
        const session = await this.getValidSession();
        if (!session?.access_token) {
          window.location.href = '/';
          return {
            ok: false,
            error: 'Session expired. Please login again.',
            statusCode: 401
          };
        }

        // Log session details in development
        if (import.meta.env.DEV) {
          console.log('Session details:', {
            user: session.user?.email,
            token_length: session.access_token.length,
            token_start: session.access_token.substring(0, 10) + '...',
            token_end: '...' + session.access_token.substring(session.access_token.length - 10),
            expires_at: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'unknown'
          });
        }

        // Merge headers safely
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          ...(options.headers || {})  // Keep custom headers but don't overwrite auth
        };

        // Update options with credentials and safe headers
        options = {
          ...options,
          credentials: 'include',
          mode: 'cors',
          headers
        };
      } else {
        // For auth endpoints, only set basic headers
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(options.headers || {})
        };

        options = {
          ...options,
          credentials: 'include',
          mode: 'cors',
          headers
        };
      }

      // Log request details in development
      if (import.meta.env.DEV) {
        console.log('Making request to:', `${this.baseUrl}${path}`, {
          method: options.method || 'GET',
          headers: {
            ...options.headers,
            Authorization: options.headers?.Authorization ? 
              options.headers.Authorization.substring(0, 20) + '...' : undefined
          },
          body: options.body ? 
            (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : 
            undefined
        });
      }

      const response = await fetch(`${this.baseUrl}${path}`, options);

      // Log response details in development
      if (import.meta.env.DEV) {
        console.log('Response details:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
      }

      // Handle 401 without recursive refresh
      if (response.status === 401) {
        window.location.href = '/';
        return {
          ok: false,
          error: 'Session expired. Please login again.',
          statusCode: 401
        };
      }

      const data = await response.json().catch(() => ({}));

      // Log response data in development
      if (import.meta.env.DEV) {
        console.log('Response data:', data);
      }

      if (!response.ok) {
        console.error('Request failed:', {
          status: response.status,
          response: data
        });
        return {
          ok: false,
          error: data?.error || data?.detail || `${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }

      return {
        ok: true,
        ...data,
        statusCode: response.status
      };
    } catch (error) {
      console.error('Request failed:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
        statusCode: 0
      };
    }
  }

  async get<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body: any): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(path: string, body: any): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(path: string): Promise<ApiResponse<T>> {
    return this.request<T>(path, {
      method: 'DELETE',
    });
  }

  async uploadFile(file: File, fileType: 'justo' | 'uber'): Promise<ApiResponse> {
    const session = await this.getValidSession();
    if (!session?.access_token) {
      window.location.href = '/';
      return {
        ok: false,
        error: 'Session expired. Please login again.',
        statusCode: 401
      };
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileType', fileType);

    if (import.meta.env.DEV) {
      console.log('File upload request:', {
        url: `${this.baseUrl}/files/uploadFile`,
        token_length: session.access_token.length,
        token_preview: `${session.access_token.substring(0, 10)}...${session.access_token.substring(session.access_token.length - 10)}`,
        file_name: file.name,
        file_size: file.size,
        fileType
      });
    }

    try {
      const response = await fetch(`${this.baseUrl}/files/uploadFile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: formData
      });

      const data = await response.json().catch(() => ({}));

      if (import.meta.env.DEV) {
        console.log('File upload response:', {
          status: response.status,
          data: data
        });
      }

      if (!response.ok) {
        console.error('File upload failed:', {
          status: response.status,
          response: data
        });
        return {
          ok: false,
          error: data?.error || data?.detail || `${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }

      return {
        ok: true,
        data: data,
        statusCode: response.status
      };
    } catch (error) {
      console.error('File upload request failed:', error);
      return {
        ok: false,
        error: error instanceof Error ? error.message : 'Network error during file upload',
        statusCode: 0
      };
    }
  }
}

export const api = new Api('https://centro-de-comando-api.fly.dev'); 